import type React from 'react'
import {
  FeatureConfigDefaultTaskState,
  FeatureConfigKey,
} from '../../../types/qualityAuditWorkflows.types.ts'
import { QualityAuditWorkflowConfigColumnModalFeatureInputPAndR } from '../components/QualityAuditWorkflowConfigColumnModalFeatureInputPAndR.tsx'
import { QualityAuditWorkflowConfigColumnModalFeatureInputRecordAttributeToCatflow } from '../components/QualityAuditWorkflowConfigColumnModalFeatureInputRecordAttributeToCatflow.tsx'

export enum FeatureConfigTagKey {
  Camp = 'camp',
  Publishing = 'publishing',
  Streaming = 'streaming',
}

export const FeatureConfigTagDetails: {
  [tag: string]: {
    description: string
  }
} = {
  [FeatureConfigTagKey.Streaming]: {
    description: 'This feature flag only applies to workflows whose tasks are streamed in.',
  },
  [FeatureConfigTagKey.Camp]: {
    description: 'This feature flag only applies to workflows connected to Camp.',
  },
  [FeatureConfigTagKey.Publishing]: {
    description: 'When a batch is completed we will publish the result.',
  },
}

export enum FeatureConfigType {
  Boolean = 'boolean',
  Int = 'int',
  String = 'string',
  Enum = 'enum',
  Custom = 'custom',
}

export interface FeatureConfigDetail {
  shortDescription?: string
  type: FeatureConfigType
  tags?: FeatureConfigTagKey[]
  enums?: string[]
  customComponent?: React.ComponentType<{
    featureValue?: string
    onChange: (value: string) => void
  }>
}

export const FeatureConfigDetails: {
  [configKey: string]: FeatureConfigDetail
} = {
  [FeatureConfigKey.OutsourceVendor]: {
    type: FeatureConfigType.String,
    shortDescription: 'When applied an outsource team can view and work on the batch (eg. Sama). ',
  },
  [FeatureConfigKey.OnlySamaManagerCompleteBatch]: {
    type: FeatureConfigType.Boolean,
    shortDescription: 'Regular Sama users cannot complete a batch, only a Sama manager can.',
  },
  [FeatureConfigKey.NoAuditWithoutAssigned]: {
    type: FeatureConfigType.Boolean,
    shortDescription: 'Disables auditing until it has been assigned to a user.',
  },
  [FeatureConfigKey.ShowApproved]: {
    type: FeatureConfigType.Boolean,
    shortDescription: 'Determines if we will show the approve icon under the state column.',
  },
  [FeatureConfigKey.ShowRejected]: {
    type: FeatureConfigType.Boolean,
    shortDescription: 'Determines if we will show the reject icon under the state column.',
  },
  [FeatureConfigKey.DisableReject]: {
    type: FeatureConfigType.Boolean,
    shortDescription: 'Disables clicking on the reject icon under the state columns.',
  },
  [FeatureConfigKey.ShowInternalCommentFilter]: {
    type: FeatureConfigType.Boolean,
    shortDescription: 'Controls whether we allow auditors to enter in an internal comment.',
  },
  [FeatureConfigKey.ShowExternalCommentFilter]: {
    type: FeatureConfigType.Boolean,
    shortDescription: 'Controls whether we allow auditors to enter in an external comment.',
  },
  [FeatureConfigKey.PrecisionAndRecall]: {
    type: FeatureConfigType.Custom,
    shortDescription: 'Enables a workflow for Precision and Recall.',
    tags: [FeatureConfigTagKey.Camp],
    customComponent: QualityAuditWorkflowConfigColumnModalFeatureInputPAndR,
  },
  [FeatureConfigKey.MaxAccumulatingDays]: {
    type: FeatureConfigType.Int,
    shortDescription:
      'When set, we will only create batches if the tasks created_at timestamp is older than the specified value in days.',
    tags: [FeatureConfigTagKey.Camp, FeatureConfigTagKey.Streaming],
  },
  [FeatureConfigKey.EnableAccumulatingTasks]: {
    type: FeatureConfigType.Boolean,
    shortDescription:
      'When tasks are streamed in we will wait until we accumulate enough tasks to create a batch.',
    tags: [FeatureConfigTagKey.Camp, FeatureConfigTagKey.Streaming],
  },
  [FeatureConfigKey.MinTasksPerBatch]: {
    type: FeatureConfigType.Int,
    shortDescription: 'The minimum number of tasks required to trigger batch creation.',
    tags: [FeatureConfigTagKey.Camp, FeatureConfigTagKey.Streaming],
  },
  [FeatureConfigKey.MaxTasksPerBatch]: {
    type: FeatureConfigType.Int,
    shortDescription: 'The maximum number of tasks a batch can hold.',
    tags: [FeatureConfigTagKey.Camp, FeatureConfigTagKey.Streaming],
  },
  [FeatureConfigKey.IsolateBySubmissionUuid]: {
    type: FeatureConfigType.Boolean,
    shortDescription: 'When set, we will isolate batch creation by submission uuids.',
    tags: [FeatureConfigTagKey.Camp, FeatureConfigTagKey.Streaming],
  },
  [FeatureConfigKey.EnableDeduplicatingEntityIds]: {
    type: FeatureConfigType.Boolean,
    shortDescription:
      'When set, we will periodically dedup tasks whose entity ids are the same (only for pending tasks).',
  },
  [FeatureConfigKey.EnableDeduplicatingBulkEditTaxonomyRequests]: {
    type: FeatureConfigType.Boolean,
    shortDescription: 'When set, we will periodically dedup taxonomy tasks made from bulk edits.',
  },
  [FeatureConfigKey.UseSequentialSamplingForReview]: {
    type: FeatureConfigType.Enum,
    shortDescription: 'Determines the order of which we sample tasks from in a batch.',
    enums: ['start', 'end'],
  },
  [FeatureConfigKey.RecordAttributesToCatflow]: {
    type: FeatureConfigType.Custom,
    shortDescription: 'Allows you to publish attributes to Catalog via Catflow.',
    tags: [FeatureConfigTagKey.Publishing],
    customComponent: QualityAuditWorkflowConfigColumnModalFeatureInputRecordAttributeToCatflow,
  },
  [FeatureConfigKey.SetDefaultTaskState]: {
    type: FeatureConfigType.Enum,
    shortDescription: 'Sets the default state of task when we create batches.',
    enums: [FeatureConfigDefaultTaskState.Rejected, FeatureConfigDefaultTaskState.Approved],
  },
}
